import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';


import { getUserAvatarUrl, getUserDisplayName, getUserInitials } from '../services/userService';

const NavBar = ({
  brand,
  navLinks = [],
  searchPlaceholder,
  onAccountClick = () => {},
  onLogout = () => {},
  onPreferencesClick = null,
  preferencesLabel = 'My Preferences',
  onSwitchLanguage = () => {},
  switchLangLabel = 'עברית',
  showLanguageSwitch = true,
  currentUser = null,
}) => {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const accountDisplayName = getUserDisplayName(currentUser);
  const accountAvatarUrl = getUserAvatarUrl(currentUser);
  const accountInitials = getUserInitials(currentUser);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  useEffect(() => {
    setIsAccountMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [currentUser]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAccountMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  const resolveDestination = (link) => link.to ?? link.href ?? '#';

  const isLinkActive = (link, destination) => {
    if (typeof link.isActive === 'boolean') {
      return link.isActive;
    }

    if (!destination || destination === '#') {
      return false;
    }

    if (!destination.startsWith('/')) {
      return false;
    }

    const normalizedDestination = destination.replace(/\/+$/, '') || '/';

    // Exact match for root-like destinations ("/", "/heb") to avoid them
    // matching every path that starts with their prefix.
    const isRootLike = normalizedDestination === '/' || /^\/[a-z]{2,4}$/.test(normalizedDestination);
    if (isRootLike) {
      return normalizedPath === normalizedDestination;
    }

    return normalizedPath.startsWith(normalizedDestination);
  };

  const renderLink = (link) => {
    const destination = resolveDestination(link);
    const active = isLinkActive(link, destination);
    const className = `nav-link${active ? ' is-active' : ''}${link.disabled ? ' is-disabled' : ''}`;

    if (link.onClick) {
      return (
        <button key={link.label} type="button" className={className} onClick={link.onClick}>
          {link.label}
        </button>
      );
    }

    if (destination.startsWith('/')) {
      return (
        <Link key={link.label} to={destination} className={className} aria-current={active ? 'page' : undefined}>
          {link.label}
        </Link>
      );
    }

    return (
      <a key={link.label} href={destination} className={className}>
        {link.label}
      </a>
    );
  };

  const handleAccountButtonClick = () => {
    if (currentUser) {
      setIsAccountMenuOpen((previousState) => !previousState);
      return;
    }

    onAccountClick();
  };

  const handleLogoutClick = async () => {
    setIsAccountMenuOpen(false);
    await onLogout();
  };

  return (
    <nav className="nav-shell">
      <div className="nav-content">
        <button
          type="button"
          className={`icon-button hamburger-button${isMobileMenuOpen ? ' is-open' : ''}`}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
        >
          <span className="material-symbols-rounded">
            {isMobileMenuOpen ? 'close' : 'menu'}
          </span>
        </button>
        <div className="brand-mark">{brand}</div>
        <div className="nav-links" aria-label="Primary navigation">
          {navLinks.map((link) => renderLink(link))}
        </div>
        <div className="nav-actions">
          {showLanguageSwitch ? (
            <button
              type="button"
              className="lang-switch-button"
              onClick={onSwitchLanguage}
              aria-label="Switch language"
            >
              {switchLangLabel}
            </button>
          ) : null}
          <div className="account-menu-shell" ref={accountMenuRef}>
            <button
              type="button"
              className={`icon-button account-button${currentUser ? ' is-authenticated' : ''}`}
              aria-label={currentUser ? `${accountDisplayName} account` : 'Account'}
              title={currentUser ? accountDisplayName : 'Sign in'}
              onClick={handleAccountButtonClick}
            >
              {currentUser ? (
                accountAvatarUrl ? (
                  <img src={accountAvatarUrl} alt={accountDisplayName} className="account-avatar" />
                ) : (
                  <span className="account-initials" aria-hidden="true">
                    {accountInitials}
                  </span>
                )
              ) : (
                <span className="material-symbols-rounded">account_circle</span>
              )}
            </button>
            {currentUser && isAccountMenuOpen ? (
              <div className="account-menu" role="menu" aria-label="Account options">
                {onPreferencesClick && (
                  <button
                    type="button"
                    className="account-menu-item"
                    onClick={() => { setIsAccountMenuOpen(false); onPreferencesClick(); }}
                  >
                    {preferencesLabel}
                  </button>
                )}
                <button type="button" className="account-menu-item" onClick={handleLogoutClick}>
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="nav-divider" />
      {isMobileMenuOpen && (
        <div className="mobile-nav-menu" aria-label="Mobile navigation">
          {navLinks.map((link) => renderLink(link))}
          {showLanguageSwitch ? (
            <div className="mobile-nav-footer">
              <button
                type="button"
                className="lang-switch-button"
                onClick={() => { setIsMobileMenuOpen(false); onSwitchLanguage(); }}
                aria-label="Switch language"
              >
                {switchLangLabel}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </nav>
  );
};

export default NavBar;
